import _startCase from 'lodash/startCase';
import type { TupleToUnion } from 'type-fest';

import { defineSort } from '@utils/url-filters';

import {
  AppVersionStatus,
  type ContentsListFilters,
  FixState,
  VexStatus,
  type VulnerabilitiesFilters,
} from './applications.models';

export const APPLICATION_DETAIL_TABS = [
  'summary',
  'compliance',
  'contents',
  'vulnerabilities',
] as const;

export type ApplicationDetailTab = TupleToUnion<typeof APPLICATION_DETAIL_TABS>;

export const VERSION_STATUS_LABELS: Record<AppVersionStatus, string> = {
  [AppVersionStatus.Eol]: 'End of Life',
  [AppVersionStatus.InProgress]: 'In Progress',
  [AppVersionStatus.Released]: 'Released',
};

export const FIX_STATE_LABELS: Record<FixState, string> = {
  [FixState.Fixed]: 'Fixed',
  [FixState.NotFixed]: 'Not Fixed',
  [FixState.Unknown]: 'Unknown',
  [FixState.WontFix]: "Won't Fix",
};

/**
 * `EnumList` filter options for VEX status. The leading `none` option matches
 * items with no VEX annotation.
 */
export const VEX_STATUS_FILTER_OPTIONS = [
  { label: 'None', value: 'none' },
  ...Object.values(VexStatus).map(value => ({
    label: _startCase(value),
    value,
  })),
];

/**
 * Default to the VEX statuses that still warrant attention — packages with
 * no annotation, packages explicitly marked `affected`, and packages under
 * triage. Hides `not_affected` and `fixed` annotations from the initial
 * view; the user can clear or extend the pill to see those.
 */
export const DEFAULT_VULNERABILITY_FILTERS: VulnerabilitiesFilters = {
  vexStatus: ['none', VexStatus.Affected, VexStatus.UnderInvestigation],
};

export const DEFAULT_CONTENTS_FILTERS: ContentsListFilters = {};

// --- URL-reflected sort contracts ------------------------------------------
// Each tab's service builds its sort schema from these keys, so a column the
// URL names but the endpoint can't serve is dropped on the way in rather than
// rejected on the way out. Server bundles import these; keep the file free of
// component and stylesheet imports.

/**
 * Sortable columns and fallback sort for the Compliance tab's Policy Findings.
 */
export const COMPLIANCE_SORT = defineSort(
  [
    'allowlisted',
    'assetsAffected',
    'outcome',
    'policyRule',
    'trigger',
    'vulnerability',
  ],
  { outcome: true },
);

/**
 * Sortable columns and fallback sort for the Contents tab's package list.
 */
export const CONTENTS_SORT = defineSort(['assetCount', 'name', 'purl'], {
  name: false,
});

/**
 * Sortable columns and fallback sort for the Vulnerabilities tab.
 */
export const VULNERABILITIES_SORT = defineSort(
  [
    'affectedPackages',
    'anchoreScore',
    'cvssScore',
    'epssScore',
    'hasFix',
    'hasVexAnnotation',
    'kev',
    'lastFound',
    'severity',
    'vulnerabilityId',
  ],
  { anchoreScore: true },
);

export const APPLICATIONS_TABLE_COLUMNS = [
  'Application Name',
  'Latest Version',
  'Latest Version Created',
  'Versions',
  'Created',
  'Description',
  'Contact',
  '',
];

export const VERSIONS_TABLE_COLUMNS = [
  'Version Name',
  'Description',
  'Assets',
  'Created',
  'Release Date',
  'Status',
  '',
];

export const POLICY_FINDINGS_TABLE_COLUMN_TITLES = {
  TRIGGER: 'Trigger',
  VULNERABILITY: 'Vulnerability',
  DETAILS: 'Details',
  OUTCOME: 'Outcome',
  POLICY_RULE: 'Policy Rule',
  AFFECTED_ASSETS: 'Affected Assets',
  ALLOWLIST: 'Allowlist',
} as const;

export const ASSETS_TABLE_COLUMN_TITLES = {
  NAME: 'Asset Name',
  TYPE: 'Type',
  ANNOTATIONS: 'Annotations',
  ADDED: 'Added',
} as const;

export const VULNERABILITIES_TABLE_COLUMN_TITLES = {
  ANCHORE_SCORE: 'Anchore Score',
  VULNERABILITY: 'Vulnerability',
  DERIVED_SEVERITY: 'Derived Severity',
  MAX_CVSS: 'Max CVSS',
  EPSS: 'EPSS',
  KEV: 'KEV',
  LAST_FOUND: 'Last Found',
  FIX: 'Fix',
  ANNOTATED: 'Annotated',
  PACKAGES: 'Packages',
} as const;

export const VERSION_METRIC_TITLES = {
  PACKAGES: 'Packages',
  VULNERABILITIES: 'Vulnerabilities',
  AVAILABLE_FIXES: 'Vulnerabilities with Available Fixes',
  CISA_KEV: 'Vulnerabilities on the CISA KEV List',
  EPSS_SCORES: 'Vulnerabilities with EPSS Scores',
} as const;
